import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChevronDown, ExternalLink, MapPin, Clock, Quote, Award, BarChart3,
  Calendar as CalendarIcon, Ticket, Image as ImageIcon, Youtube
} from 'lucide-react';

/** Parse a YouTube URL → embed URL */
const ytEmbed = (url = '') => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

/** Lightweight markdown for text blocks: paragraphs + **bold** + bullets */
const renderText = (s = '') => s.split(/\n\n+/).map((para, i) => {
  if (para.trim().startsWith('- ')) {
    const items = para.split('\n').map(l => l.replace(/^-\s+/, '').trim()).filter(Boolean);
    return <ul key={i} className="list-disc pl-5 space-y-1">{items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
  }
  const html = para
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
  return <p key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
});

const Countdown = ({ target }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!target) return;
    const tick = () => setT(Math.max(0, new Date(target).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t % 86400000) / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  if (!target) return null;
  if (t <= 0) return <p className="text-2xl font-bold text-emerald-500">Event has started!</p>;
  return (
    <div className="grid grid-cols-4 gap-3 max-w-md mx-auto" data-testid="countdown-block">
      {[['Days', d], ['Hours', h], ['Mins', m], ['Secs', s]].map(([lbl, v]) => (
        <div key={lbl} className="text-center p-4 rounded-xl bg-gradient-to-br from-indigo-500/10 to-emerald-500/5 border border-border">
          <div className="text-3xl font-bold tabular-nums">{String(v).padStart(2, '0')}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{lbl}</div>
        </div>
      ))}
    </div>
  );
};

const FaqBlock = ({ items = [] }) => {
  const [open, setOpen] = useState({});
  return (
    <div className="space-y-2" data-testid="faq-block">
      {items.map((it, i) => (
        <div key={i} className="border border-border rounded-lg overflow-hidden">
          <button onClick={() => setOpen(p => ({ ...p, [i]: !p[i] }))} className="w-full p-4 text-left flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors">
            <span className="font-medium text-sm">{it.q}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${open[i] ? 'rotate-180' : ''}`} />
          </button>
          {open[i] && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{it.a}</div>}
        </div>
      ))}
    </div>
  );
};

const Lightbox = ({ src, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out" onClick={onClose}>
    <img src={src} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" />
  </div>
);

const Gallery = ({ images = [] }) => {
  const [zoom, setZoom] = useState(null);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="gallery-block">
        {images.map((img, i) => (
          <button key={i} onClick={() => setZoom(img.url)} className="aspect-square overflow-hidden rounded-lg border border-border group">
            <img src={img.url} alt={img.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          </button>
        ))}
      </div>
      {zoom && <Lightbox src={zoom} onClose={() => setZoom(null)} />}
    </>
  );
};

const Block = ({ block, event }) => {
  const { type, data = {}, is_visible } = block;
  if (!is_visible) return null;
  const heading = data.heading;

  // Hero block renders full-width without the section wrapper
  if (type === 'hero') {
    const img = data.image_url || event?.image_url || '';
    const title = data.title_override || event?.name || 'Event';
    const tagline = data.tagline_override || event?.tagline || '';
    const dateText = (() => {
      const d = data.date_override || event?.event_date;
      if (!d) return '';
      try { return new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); } catch { return d; }
    })();
    const venueText = data.venue_override || (typeof event?.venue === 'object' ? event?.venue?.name : event?.venue) || '';
    const align = data.alignment || 'center'; // left | center
    const textCls = align === 'left' ? 'text-left items-start' : 'text-center items-center';
    return (
      <div className="relative -mx-4 sm:-mx-6 -mt-6 mb-2 overflow-hidden rounded-xl" data-testid="hero-block">
        <div className="relative h-[50vh] min-h-[380px] flex flex-col justify-end">
          {img ? (
            <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-emerald-600" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
          <div className={`relative z-10 p-6 sm:p-10 max-w-4xl flex flex-col gap-2 ${textCls} ${align === 'center' ? 'mx-auto' : ''}`}>
            {data.eyebrow && <p className="text-xs uppercase tracking-[0.2em] text-white/80 font-semibold">{data.eyebrow}</p>}
            <h1 className="text-3xl sm:text-5xl font-bold text-white drop-shadow-lg">{title}</h1>
            {tagline && <p className="text-base sm:text-lg text-white/90 max-w-2xl drop-shadow">{tagline}</p>}
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-white/90">
              {dateText && <div className="flex items-center gap-1.5"><CalendarIcon className="h-4 w-4" /> {dateText}</div>}
              {venueText && <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {venueText}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const wrap = (children) => (
    <section className="space-y-4">
      {heading && <h2 className="text-2xl font-bold">{heading}</h2>}
      {children}
    </section>
  );

  switch (type) {
    case 'text':
      return wrap(<div className="prose-sm max-w-none text-sm space-y-3">{renderText(data.content || '')}</div>);
    case 'faq':
      return wrap(<FaqBlock items={data.items || []} />);
    case 'gallery':
      return wrap(<Gallery images={data.images || []} />);
    case 'links':
      return wrap(
        <div className="grid sm:grid-cols-2 gap-3" data-testid="links-block">
          {(data.items || []).map((it, i) => (
            <a key={i} href={it.url} target="_blank" rel="noopener noreferrer" className="block border border-border rounded-lg p-4 hover:border-indigo-500/40 transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm group-hover:text-indigo-400 transition-colors">{it.title}</p>
                  {it.description && <p className="text-xs text-muted-foreground mt-1">{it.description}</p>}
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </div>
            </a>
          ))}
        </div>
      );
    case 'youtube': {
      const src = ytEmbed(data.url);
      if (!src) return null;
      return wrap(
        <div className="aspect-video rounded-xl overflow-hidden border border-border" data-testid="youtube-block">
          <iframe src={src} title={data.title || 'Video'} className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
        </div>
      );
    }
    case 'countdown':
      return wrap(<Countdown target={data.target_datetime || event?.event_date} />);
    case 'schedule':
      return wrap(
        <div className="space-y-3" data-testid="schedule-block">
          {(data.items || []).map((it, i) => (
            <div key={i} className="flex gap-4 pb-3 border-b border-border last:border-0">
              <div className="w-20 flex-shrink-0 text-sm font-mono font-semibold text-indigo-400">{it.time}</div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{it.title}</p>
                {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
              </div>
            </div>
          ))}
        </div>
      );
    case 'lineup':
      return wrap(
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="lineup-block">
          {(data.items || []).map((p, i) => (
            <div key={i} className="text-center">
              {p.image_url && <div className="aspect-square overflow-hidden rounded-full mb-2 border-2 border-indigo-500/30 mx-auto max-w-[140px]"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>}
              <p className="font-semibold text-sm">{p.name}</p>
              {p.role && <p className="text-xs text-muted-foreground">{p.role}</p>}
              {p.set_time && <p className="text-[11px] text-indigo-400 font-mono mt-0.5">{p.set_time}</p>}
            </div>
          ))}
        </div>
      );
    case 'sponsors':
      return wrap(
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4 items-center" data-testid="sponsors-block">
          {(data.items || []).map((s, i) => {
            const inner = s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-12 w-full object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" loading="lazy" /> : <div className="h-12 flex items-center justify-center text-sm font-semibold text-muted-foreground">{s.name}</div>;
            return s.url ? <a key={i} href={s.url} target="_blank" rel="noopener noreferrer">{inner}</a> : <div key={i}>{inner}</div>;
          })}
        </div>
      );
    case 'testimonials':
      return wrap(
        <div className="grid sm:grid-cols-2 gap-4" data-testid="testimonials-block">
          {(data.items || []).map((t, i) => (
            <Card key={i} className="border-border">
              <CardContent className="p-5">
                <Quote className="h-6 w-6 text-indigo-400/40 mb-2" />
                <p className="text-sm leading-relaxed italic">"{t.quote}"</p>
                <p className="text-xs font-semibold mt-3">— {t.author}{t.source && <span className="text-muted-foreground font-normal">, {t.source}</span>}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    case 'map': {
      const addr = data.address || '';
      const src = `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
      return wrap(
        <div className="rounded-xl overflow-hidden border border-border" data-testid="map-block">
          <iframe src={src} title="Map" className="w-full h-72 border-0" loading="lazy" />
          {addr && <p className="text-xs text-muted-foreground p-3 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {addr}</p>}
        </div>
      );
    }
    case 'cta': {
      const url = data.button_url || '#tickets';
      return (
        <div className="rounded-2xl p-8 text-center bg-gradient-to-br from-indigo-500 to-emerald-500 text-white" data-testid="cta-block">
          {data.heading && <h3 className="text-2xl font-bold mb-2">{data.heading}</h3>}
          {data.text && <p className="mb-5 opacity-90">{data.text}</p>}
          <a href={url} target={url.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-white/90 font-semibold">
              <Ticket className="h-4 w-4 mr-2" /> {data.button_text || 'Get Tickets'}
            </Button>
          </a>
        </div>
      );
    }
    case 'stats':
      return wrap(
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="stats-block">
          {(data.items || []).map((s, i) => (
            <div key={i} className="text-center p-4 rounded-xl border border-border bg-muted/20">
              <div className="text-3xl font-bold text-indigo-500">{s.number}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
};

export default function EventPageBlocks({ blocks = [], event }) {
  if (!blocks?.length) return null;
  return (
    <div className="space-y-12 mt-10" data-testid="event-page-blocks">
      {blocks.map((b, idx) => <Block key={b.block_id || b.id || `blk-${idx}`} block={b} event={event} />)}
    </div>
  );
}
